<template>
    <div class="login-form">
        <form
            class="form"
            @submit.prevent="login"
        >
            <slot name="title">
                <!-- TODO Extract this heading into a reusable one-->
                <!-- TODO Translate-->
                <h3 class="title">
                    Sign in to see your designs and quotations
                </h3>
            </slot>

            <TextField
                id="form-email"
                v-model="identifier"
                v-qa="'login-email'"
                :error="emailError"
                :label="t('login_modal.email')"
                class="text-field"
                required
                type="email"
            />

            <TextField
                id="form-password"
                v-model="password"
                v-qa="'login-password'"
                :error="errorMessage"
                :label="t('login_modal.password')"
                class="text-field"
                required
                type="password"
            />

            <br />

            <ThButton
                v-qa="'login-button'"
                :loading="fetching"
                class="th-button"
                fixed-width
                submit
                type="solid"
            >
                {{ t('login_modal.login_btn') }}
            </ThButton>

            <div class="gsi-separator">
                <span>{{ t('login_modal.or') }}</span>
            </div>

            <div class="gsi-wrapper">
                <div
                    ref="googleSignInButtonEl"
                    class="google-sign-in-button"
                ></div>
            </div>

            <div class="additional-actions">
                <p class="helper">
                    <ThButton
                        :to="routes.profile.magicLinkRequest()"
                        color="var(--slate)"
                        size="small"
                        type="bordered"
                    >
                        {{ t('login_modal.magic_link_button') }}
                    </ThButton>
                </p>

                <div class="reset-password">
                    <ThLink :to="routes.signIn()">
                        {{ t('login_modal.create_account_button') }}
                    </ThLink>
                    |
                    <ThLink :to="routes.forgottenPassword()">
                        {{ t('login_modal.reset_password_button') }}
                    </ThLink>
                </div>
            </div>
        </form>
    </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'

import type {
    LoginResponseFailureFragment,
    LoginResponseSuccessFragment,
} from '@/graphql/api'
import { assertNever } from '@/helpers'
import useLogin from '@/composables/auth/useLogin'
import { useRoutes } from '@/router/routes'
import { useGoogleSignIn } from '@/composables/auth/useGoogleSignIn'

import TextField from '@/components/ui/inputs/TextField.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'
import ThLink from '@/components/ui/buttons/ThLink.vue'

const props = defineProps<{
    preFilledEmail?: string
}>()

const emit = defineEmits<{
    login: [v: LoginResponseSuccessFragment]
    error: [v: LoginResponseFailureFragment]
}>()

const {
    identifier,
    password,
    login: loginMutation,
    loginWithGoogle: loginWithGoogleMutation,
    fetching,
    errorMessage,
    emailError,
} = useLogin()

if (props.preFilledEmail) {
    identifier.value = props.preFilledEmail
}

const handleLoginResponse = (
    data?: LoginResponseSuccessFragment | LoginResponseFailureFragment
) => {
    if (!data) {
        return
    }

    switch (data.__typename) {
        case 'LoginResponseSuccess':
            emit('login', data)
            break
        case 'LoginResponseFailure':
            emit('error', data)
            break
        default:
            assertNever(data, data)
            break
    }
}

const login = () => loginMutation()?.then(handleLoginResponse)
const loginWithGoogle = () =>
    loginWithGoogleMutation().then(handleLoginResponse)

const { googleSignInButtonEl } = useGoogleSignIn(loginWithGoogle)

const routes = useRoutes()
const { t } = useI18n()
</script>

<style lang="scss" scoped>
.login-form {
    .form {
        width: 380px;
        max-width: 100%;
        margin: 0 auto;
        padding: 0 20px;

        .title {
            font-family: $parcel-font-stack;
            text-align: center;
            font-size: 1.5rem;
            line-height: 1.2em;
            color: $blue;
            margin: 0 0 40px;
        }

        .gsi-wrapper {
            margin-top: 20px;
            display: flex;
            justify-content: center;
        }

        .gsi-separator {
            text-align: center;
            display: flex;
            align-items: center;
            max-width: 200px;
            margin: 30px auto 20px;
            text-transform: uppercase;
            font-family: $effra-font-stack;
            letter-spacing: 0.05em;
            font-size: 0.875rem;

            &:before,
            &:after {
                content: '';
                display: inline-block;
                width: 50%;
                height: 1px;
                background-color: $grey-200;
            }

            span {
                display: block;
                margin: 0 10px;
            }
        }
    }

    :deep(.text-field) {
        max-width: initial;
    }

    :deep(.th-button) {
        max-width: initial;
    }

    .additional-actions {
        padding: 20px 0;
        font-size: 0.875rem;
        line-height: 1.2em;
        text-align: center;

        .helper {
            margin: 0 0 5px;
        }

        .reset-password {
            margin: 40px 0 0 0;

            :deep(a) {
                font-family: $effra-font-stack;
                letter-spacing: 0.05em;
                text-transform: uppercase;
                font-size: 0.7rem;
                margin: 0 10px;
            }
        }
    }
}
</style>
